import { getRaffle, markRaffleResultAsViewed } from "@network/Raffle";
import { RaffleParams, SearchBy } from "@network/Raffle/type";
import { RaffleDataBased, Raffle } from "@type/Raffle";
import { SCREEN } from "@type/index";
import debounce from "@utils/Debounce";
import { trackFunnel } from '@utils/analytics';
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { RAFFLE_TYPE } from "@const/dashboard";
import Loading from "@components/Loading";
import moment from "moment";
import defaultRaffleThumbnail from "@assets/img/raffle/defaultThumbnailRaffle.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faFaceFrown, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";


const initSearchParams: RaffleParams = {
  limit: 5,
  offset: 0,
  searchBy: SearchBy.TITLE,
  type: RAFFLE_TYPE.all,
  screen: SCREEN.RAFFLE_RESULT,
};

export const RaffleResult = ({ title }: { title: string }) => {
  const [raffleData, setRaffleData] = useState<RaffleDataBased>();
  const [raffleName, setRaffleName] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState<boolean>(true);
  const [mobileFilter, setMobileFilter] = useState<string>("all");
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [copiedCoupon, setCopiedCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  // const [viewingResultId, setViewingResultId] = useState<number | null | undefined>(null);
  // const [showResultModal, setShowResultModal] = useState(false);
  // const [resultModalData, setResultModalData] = useState<{
  //   isWin: boolean;
  //   title: string;
  //   selectedNumber: number;
  //   winningNumber: number;
  //   couponCode?: string;
  //   type?: string;
  //   startTime?: string | Date;
  // } | null>(null);
  // const [copiedResultCoupon, setCopiedResultCoupon] = useState(false);

  // const handleViewResult = async (raffle: Raffle) => {
  //   const raffleDrawId = raffle.drawid;
  //   setViewingResultId(raffleDrawId);

  //   try {
  //     // await markRaffleResultAsViewed(raffleDrawId);

  //     // Show result modal
  //     const isWin = raffle.selectedNumber === raffle.winning_number;
  //     setResultModalData({
  //       isWin,
  //       title: raffle.title,
  //       selectedNumber: raffle.selectedNumber!,
  //       winningNumber: raffle.winning_number!,
  //       couponCode: raffle.couponCode,
  //       type: raffle.type,
  //       startTime: raffle.startTime,
  //     });
  //     setShowResultModal(true);
  //     setCopiedResultCoupon(false);

  //     // Update the local state to mark as viewed
  //     setRaffleData((prevData) => {
  //       if (!prevData) return prevData;

  //       const updateRaffles = (raffles: Raffle[]) =>
  //         raffles.map(r =>
  //           r.drawid === raffleDrawId
  //             ? { ...r, resultViewed: true }
  //             : r
  //         );

  //       const updateCategory = (category: { raffles: Raffle[]; total: number }) => ({
  //         raffles: updateRaffles(category.raffles),
  //         total: category.total,
  //       });

  //       return {
  //         raffles: {
  //           ...prevData.raffles,
  //           daily: updateCategory(prevData.raffles.daily),
  //           weekly: updateCategory(prevData.raffles.weekly),
  //           monthly: updateCategory(prevData.raffles.monthly),
  //           quarterly: updateCategory(prevData.raffles.quarterly),
  //           halfYear: updateCategory(prevData.raffles.halfYear),
  //           annually: updateCategory(prevData.raffles.annually),
  //         }
  //       };
  //     });
  //   } catch (error) {
  //     console.error("Error marking result as viewed:", error);
  //   } finally {
  //     setViewingResultId(null);
  //   }
  // };

  const getRaffleResult = useCallback(
    async (signal: AbortSignal) => {
      try {
        setLoading(true);

        initSearchParams.keyword = raffleName;
        if (fromDate || toDate) {
          const start = fromDate
            ? moment(fromDate).utc().format("YYYY-MM-DD HH:mm:ss")
            : "";
          const end = toDate
            ? moment(toDate).utc().format("YYYY-MM-DD HH:mm:ss")
            : "";

          initSearchParams.filter = `startTime,, ${start}, ${toDate}`;
        } else {
          delete initSearchParams.filter;
        }
        const response = await getRaffle(initSearchParams, signal);
        setRaffleData(response.data);
        setTimeout(() => {
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error(error);

        setTimeout(() => {
          setLoading(false);
        }, 500);
      }
    },
    [raffleName, fromDate, toDate]
  );

  const debouncedFetchAll = debounce(getRaffleResult, 400);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    debouncedFetchAll(signal);

    return () => {
      controller.abort();
    };
  }, [raffleName, fromDate, toDate]);

  // Convert raffle data to table format
  const tableData = useMemo(() => {
    if (!raffleData?.raffles) return [];

    const allRaffles: Raffle[] = [
      ...raffleData.raffles.daily.raffles,
      ...raffleData.raffles.weekly.raffles,
      ...raffleData.raffles.monthly.raffles,
      ...raffleData.raffles.quarterly.raffles,
      ...raffleData.raffles.halfYear.raffles,
      ...raffleData.raffles.annually.raffles,
    ];

    return allRaffles;
  }, [raffleData]);

  // Filter data for mobile based on selected filter
  const filteredMobileData = useMemo(() => {
    if (!raffleData?.raffles) return [];

    if (mobileFilter === "all") {
      return tableData;
    }

    const filterMap: { [key: string]: Raffle[] } = {
      daily: raffleData.raffles.daily.raffles,
      weekly: raffleData.raffles.weekly.raffles,
      monthly: raffleData.raffles.monthly.raffles,
      quarterly: raffleData.raffles.quarterly.raffles,
      halfYear: raffleData.raffles.halfYear.raffles,
      annually: raffleData.raffles.annually.raffles,
    };

    return filterMap[mobileFilter] || [];
  }, [raffleData, mobileFilter, tableData]);

  const getDynamicRaffleImage = (raffle: any) => {
    if (!raffle) return "";

    // Only apply this logic for Daily and Weekly raffles
    if (raffle.type?.toLowerCase() !== 'daily' && raffle.type?.toLowerCase() !== 'weekly') {
      return raffle.thumbnailImg || defaultRaffleThumbnail;
    }

    const startDate = moment(raffle.startTime).local();
    const dayName = startDate.format('dddd').toUpperCase();

    // For results page, if winning_number exists, it's effectively DONE
    const hasResult = raffle.winning_number !== null && raffle.winning_number !== undefined;
    const isDone = hasResult;
    const isLive = !hasResult; // If no result yet, it might still be live or happening

    // Mapping based on assets in public/raffle/
    switch (dayName) {
      case "MONDAY":
        if (isDone) return "/raffle/MONDAY 06 draw over.png";
        return "/raffle/MONDAY 06 APRIL LIVE.png";
      case "TUESDAY":
        if (isDone) return "/raffle/TUESDAY 07 APRIL  - LIVE (2).png";
        return "/raffle/TUESDAY 07  APRIL-COMING SOON - GREYED OUT.png";
      case "WEDNESDAY":
        if (isDone) return "/raffle/WEDNESDAY- 08  APRIL- LIVE (2).png";
        return "/raffle/WEDNESDAY- 08  APRIL-COMING SOON - GREYED OUT.png";
      case "THURSDAY":
        if (isDone) return "/raffle/THURSDAY 09 APRIL LIVE (2).png";
        return "/raffle/THURSDAY 09 APRIL COMING SOON.png";
      case "FRIDAY":
        if (isDone) return "/raffle/FRIDAY 10 APRIL LIVE (2).png";
        return "/raffle/FRIDAY 10 APRIL COMING SOON.png";
      case "SATURDAY":
        return "/raffle/SATURDAY 11 APRIL COMING SOON (3).png";
      case "SUNDAY":
        if (isDone) return "/raffle/SUNDAY 12 APRIL LIVE (2).png";
        return "/raffle/SUNDAY 12 APRIL COMING SOON.png";
      default:
        return raffle.thumbnailImg || defaultRaffleThumbnail;
    }
  };


  return (
    <>
      {/* Modern Hero Section */}
      <section className="relative bg-gradient-to-br from-[#4B975B] via-[#5dad6d] to-[#4B975B] overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>

        <div className="relative container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-10 sm:py-12 md:py-16 lg:py-24">
          {/* Header */}
          <div className="flex-1 text-white space-y-4 sm:space-y-5 md:space-y-6">
            <div className="inline-flex items-center gap-2 sm:gap-3 bg-white/20 backdrop-blur-sm px-4 py-2 sm:px-5 sm:py-3 rounded-full">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path
                  fillRule="evenodd"
                  d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide">Results</span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold leading-tight">
              {title}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl">
              View all your raffle results and claim your winnings
            </p>
          </div>

          {/* Search Filters */}

        </div>

        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            className="w-full h-8 md:h-12"
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none"
          >
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="#EAF0D5"
            />
          </svg>
        </div>
      </section>

      {/* Results Section */}
      {/* Results Section */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">


        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loading />
          </div>
        ) : filteredMobileData.length === 0 ? (
          /* Empty State */
          <div className="text-center py-20 bg-gradient-to-br from-gray-50 to-white rounded-3xl shadow-xl border-2 border-gray-100">
            <div className="mb-6">
              <svg
                className="w-24 h-24 mx-auto text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No Results Found</h3>
            <p className="text-gray-500">You haven't participated in any raffles yet.</p>
          </div>
        ) : (
          /* Results Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMobileData.map((raffle) => {
              const hasResult = raffle.winning_number !== null && raffle.winning_number !== undefined;
              const isWin = hasResult && raffle.selectedNumber === raffle.winning_number;
              const isPending = !hasResult;

              return (
                <div
                  key={raffle.raffle_draw_id}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-[#4B975B]/30"
                >
                  {/* Card Image & Status Badge */}
                  <div className="relative bg-gradient-to-br from-gray-100 to-gray-50">
                    <img
                      src={getDynamicRaffleImage(raffle)}
                      alt={raffle.title}
                      className="w-full object-cover"
                    />

                    {/* Status Badge Overlay */}
                    <div className="absolute top-3 right-3">
                      {isPending ? (
                        <div className="flex items-center gap-2 bg-yellow-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg animate-pulse">
                          <FontAwesomeIcon icon={faSpinner} className="text-white animate-spin" />
                          PENDING
                        </div>
                      ) : isWin ? (
                        <div className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg animate-pulse">
                          <FontAwesomeIcon icon={faCheck} className="text-white" />
                          WON! 🎉
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg animate-pulse">
                          <FontAwesomeIcon icon={faTimes} className="text-white" />
                          LOST
                        </div>
                      )}
                    </div>
                    {/* Type Badge */}
                    {/* <div className="absolute top-3 left-3">
                      <div className="bg-white/90 backdrop-blur-sm text-[#4B975B] px-3 py-1 rounded-full text-xs font-bold shadow-md">
                        {raffle.type}
                      </div>
                    </div> */}
                  </div>

                  {/* Card Content */}
                  <div className="p-3 space-y-4">
                    {/* Title & Date */}
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg mb-1 line-clamp-2 group-hover:text-[#4B975B] transition-colors">
                        {raffle.title}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        <span>{moment(raffle.used_time).format("MMM DD, YYYY • HH:mm")}</span>
                      </div>
                    </div>

                    {/* Numbers Display */}
                    <div className={`grid grid-cols-2 gap-3`}>
                      {/* Your Number */}
                      <div className="bg-gradient-to-br from-[#4B975B]/10 to-[#ACC992]/10 rounded-xl p-3 border border-[#4B975B]/20">
                        <p className="text-xs font-semibold text-gray-600 mb-1">Your Number</p>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-[#4B975B] rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">{raffle.selectedNumber}</span>
                          </div>
                        </div>
                      </div>

                      {/* Winning Number */}

                      <div className={`rounded-xl p-3 border ${isPending
                        ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200'
                        : isWin
                          ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
                          : 'bg-gradient-to-br from-red-50 to-pink-50 border-red-200'
                        }`}>
                        <p className="text-xs font-semibold text-gray-600 mb-1">Winning Number</p>
                        <div className="flex items-center gap-2">
                          {isPending ? (
                            <div className="flex items-center gap-2 text-yellow-600">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              <span className="font-bold text-sm">Pending</span>
                            </div>
                          ) : (
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isWin ? 'bg-green-500' : 'bg-red-500'
                              }`}>
                              <span className="text-white font-bold text-sm">{raffle.winning_number}</span>
                            </div>
                          )}
                        </div>
                      </div>

                    </div>

                    {/* Result Status */}
                    {!isPending && (
                      <div className={`rounded-xl p-4 text-center ${isWin
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200'
                        : 'bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-200'
                        }`}>
                        {isWin ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center gap-2 text-green-700">
                              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <span className="text-lg font-extrabold">Congratulations!</span>
                            </div>
                            <p className="text-sm text-green-600 font-medium">You won this raffle! 🎊</p>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2 text-gray-600">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm font-semibold">Better luck next time!</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* CTA Button */}
                    {isPending ? (
                      <button
                        disabled
                        className="w-full bg-gray-100 text-gray-400 py-3.5 px-4 rounded-xl font-bold text-base cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Awaiting Results
                      </button>
                    ) : isWin ? (
                      <button
                        onClick={() => {
                          setCouponCode(raffle.couponCode || "LOYALTYTEST01");
                          setShowCouponModal(true);
                          setCopiedCoupon(false);
                        }}
                        className="w-full bg-gradient-to-r from-[#4B975B] to-[#5dad6d] text-white py-3.5 px-4 rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 group"
                      >
                        <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                        </svg>
                        Claim Your Reward
                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        disabled
                        className="w-full bg-gray-100 text-gray-500 py-3.5 px-4 rounded-xl font-semibold text-base cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                        </svg>
                        No Prize
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      { /* Coupon Modal */}
      {showCouponModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/50 backdrop-blur-sm animate-fadeIn"
          onClick={() => setShowCouponModal(false)} // Close modal on outside click
        >
          <div
            className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-scaleIn"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
          >
            {/* Confetti Background Effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-0 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-confetti-1"></div>
              <div className="absolute top-0 left-1/2 w-2 h-2 bg-green-400 rounded-full animate-confetti-2"></div>
              <div className="absolute top-0 left-3/4 w-2 h-2 bg-blue-400 rounded-full animate-confetti-3"></div>
              <div className="absolute top-0 left-1/3 w-2 h-2 bg-red-400 rounded-full animate-confetti-4"></div>
              <div className="absolute top-0 left-2/3 w-2 h-2 bg-purple-400 rounded-full animate-confetti-5"></div>
            </div>

            {/* Close Button */}
            <button
              onClick={() => setShowCouponModal(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-sm sm:text-base text-gray-600" />
            </button>

            {/* Header with Gradient */}
            <div className="relative bg-gradient-to-br from-[#4B975B] via-[#5dad6d] to-[#4B975B] p-6 sm:p-8 text-center">
              {/* Trophy Icon with Animation */}
              <div className="mb-3 sm:mb-4 animate-bounce-slow">
                <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-sm rounded-full">
                  <svg className="w-10 h-10 sm:w-12 sm:h-12 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-1.5 sm:mb-2 animate-pulse">
                🎉 Congratulations! 🎉
              </h2>
              <p className="text-white/90 text-base sm:text-lg font-medium">
                You've Won the Raffle!
              </p>
            </div>

            {/* Content */}
            <div className="p-3 sm:p-6 md:p-8 space-y-4 sm:space-y-5 md:space-y-6">
              {/* Success Message */}
              <div className="text-center space-y-1.5 sm:space-y-2">
                <p className="text-gray-700 text-base sm:text-lg font-semibold">
                  Here's your exclusive coupon code:
                </p>
              </div>

              {/* Coupon Code Display */}
              <div className="relative">
                <div className="bg-gradient-to-br from-[#4B975B]/10 to-[#5dad6d]/10 border-2 border-dashed border-[#4B975B] rounded-xl sm:rounded-2xl p-2 sm:p-5 md:p-6 text-center">
                  <div className="inline-flex items-center gap-2 sm:gap-3 bg-white rounded-lg sm:rounded-xl px-4 py-3 sm:px-6 sm:py-4 shadow-lg">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-[#4B975B] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clipRule="evenodd" />
                    </svg>
                    <span className="text-lg sm:text-xl md:text-2xl font-black text-[#4B975B] tracking-wider font-mono break-all">
                      {couponCode}
                    </span>
                  </div>
                </div>

                {/* Sparkle Effects */}
                <div className="absolute -top-1.5 -left-1.5 sm:-top-2 sm:-left-2 text-base sm:text-lg text-yellow-400 animate-ping-slow">✨</div>
                <div className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 text-base sm:text-lg text-yellow-400 animate-ping-slow" style={{ animationDelay: '0.5s' }}>✨</div>
                <div className="absolute -bottom-1.5 -left-1.5 sm:-bottom-2 sm:-left-2 text-base sm:text-lg text-yellow-400 animate-ping-slow" style={{ animationDelay: '1s' }}>✨</div>
                <div className="absolute -bottom-1.5 -right-1.5 sm:-bottom-2 sm:-right-2 text-base sm:text-lg text-yellow-400 animate-ping-slow" style={{ animationDelay: '1.5s' }}>✨</div>
              </div>

              {/* Copy Button */}
              <button
                onClick={() => {
                  navigator.clipboard.writeText(couponCode);
                  setCopiedCoupon(true);
                  trackFunnel.copyCode("coupon");
                  setTimeout(() => setCopiedCoupon(false), 2000);
                }}
                className="w-full bg-gradient-to-r from-[#4B975B] to-[#5dad6d] text-white py-3 sm:py-3.5 md:py-4 px-4 sm:px-5 md:px-6 rounded-lg sm:rounded-xl font-bold text-sm sm:text-base md:text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 sm:gap-3 group"
              >
                {copiedCoupon ? (
                  <>
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Copied to Clipboard!
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy Coupon Code
                  </>
                )}
              </button>

              {/* Info Text */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <div className="flex items-start gap-2 sm:gap-3">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-xs sm:text-sm text-blue-800">
                    <p className="font-semibold mb-1">How to use your coupon on DaburShop.com:</p>
                    <ul className="list-disc list-inside space-y-0.5 sm:space-y-1 text-blue-700">
                      <li>Visit <a href="https://www.daburshop.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">www.daburshop.com</a></li>
                      <li>Copy the code above</li>
                      <li>Apply it at checkout</li>
                      <li>Enjoy your reward!</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* {showResultModal && resultModalData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/60 backdrop-blur-sm animate-fadeIn" onClick={() => setShowResultModal(false)} >
          <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-scaleIn" onClick={(e) => e.stopPropagation()}>
            
            <button
              onClick={() => setShowResultModal(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-sm sm:text-base text-gray-600" />
            </button>

            {resultModalData.isWin ? (
              <>
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute top-0 left-1/4 w-3 h-3 bg-yellow-400 rounded-full animate-confetti-1"></div>
                  <div className="absolute top-0 left-1/2 w-3 h-3 bg-green-400 rounded-full animate-confetti-2"></div>
                  <div className="absolute top-0 left-3/4 w-3 h-3 bg-blue-400 rounded-full animate-confetti-3"></div>
                  <div className="absolute top-0 left-1/3 w-3 h-3 bg-red-400 rounded-full animate-confetti-4"></div>
                  <div className="absolute top-0 left-2/3 w-3 h-3 bg-purple-400 rounded-full animate-confetti-5"></div>
                  <div className="absolute top-10 left-1/5 w-2 h-2 bg-pink-400 rounded-full animate-confetti-1" style={{ animationDelay: '0.2s' }}></div>
                  <div className="absolute top-10 right-1/5 w-2 h-2 bg-orange-400 rounded-full animate-confetti-2" style={{ animationDelay: '0.3s' }}></div>
                </div>

                <div className="relative bg-gradient-to-r from-[#4B975B] to-[#5DAD6D] p-6 text-center overflow-hidden">
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-40 h-40 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
                  </div>

                  <div className="mb-4 animate-bounce-slow">
                    <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-white/20 backdrop-blur-sm rounded-full">
                      <svg className="w-12 h-12 sm:w-14 sm:h-14 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </div>
                  </div>

                  <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3 animate-pulse">
                    🎉 YOU WON! 🎉
                  </h2>
                  <p className="text-white/90 text-lg sm:text-xl font-medium mb-2">
                    Congratulations!
                  </p>
                  <p className="text-white/80 text-base">
                    {resultModalData.title}
                  </p>
                </div>

                <div className="!p-2 sm:!p-6 md:!p-8 lg:!p-10 space-y-4 sm:space-y-6">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl sm:rounded-2xl !p-2 sm:!p-6 md:!p-8 lg:!p-10">
                    <p className="text-center text-gray-700 font-semibold mb-2 sm:mb-4">Number Match!</p>
                    <div className="flex items-center justify-center gap-3 md:gap-4">
                      <div className="text-center flex flex-col items-center justify-center">
                        <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-1 sm:mb-2 md:mb-3">Your Number</p>
                        <div className="w-12 h-12 md:w-16 md:h-16  bg-green-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-lg md:text-2xl">{resultModalData.selectedNumber}</span>
                        </div>
                      </div>
                      <svg className="w-6 h-6 md:w-8 md:h-8 text-green-600 animate-pulse mt-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div className="text-center flex flex-col items-center justify-center">
                        <p className="text-xs md:text-base text-gray-600 mb-1 md:mb-2">Winning Number</p>
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-green-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-lg md:text-2xl">{resultModalData.winningNumber}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {resultModalData.couponCode && (
                    <>
                      <div className="text-center">
                        <p className="text-gray-700 text-base md:text-lg font-semibold mb-3 md:mb-4">
                          Here's your reward coupon code:
                        </p>
                      </div>

                      <div className="relative">
                        <div className="bg-gradient-to-br from-green-100 to-emerald-100 border-2 border-dashed border-green-400 rounded-xl md:rounded-2xl !p-2 md:!p-6 text-center">
                          <div className="inline-flex items-center gap-2 sm:gap-3 md:gap-4 bg-white rounded-lg md:rounded-xl px-4 md:px-6 py-3 md:py-5 shadow-lg">
                            <svg className="w-5 h-5 md:w-6 md:h-6 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clipRule="evenodd" />
                            </svg>
                            <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-green-700 tracking-wider font-mono">
                              {resultModalData.couponCode}
                            </span>
                          </div>
                        </div>

                        <div className="absolute -top-1.5 -left-1.5 sm:-top-2 sm:-left-2 text-base sm:text-lg md:text-xl text-yellow-400 animate-ping-slow">✨</div>
                        <div className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 text-base sm:text-lg md:text-xl text-yellow-400 animate-ping-slow" style={{ animationDelay: '0.5s' }}>✨</div>
                        <div className="absolute -bottom-1.5 -left-1.5 sm:-bottom-2 sm:-left-2 text-base sm:text-lg md:text-xl text-yellow-400 animate-ping-slow" style={{ animationDelay: '1s' }}>✨</div>
                        <div className="absolute -bottom-1.5 -right-1.5 sm:-bottom-2 sm:-right-2 text-base sm:text-lg md:text-xl text-yellow-400 animate-ping-slow" style={{ animationDelay: '1.5s' }}>✨</div>
                      </div>

                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(resultModalData.couponCode!);
                          setCopiedResultCoupon(true);
                          trackFunnel.copyCode("coupon_result");
                          setTimeout(() => setCopiedResultCoupon(false), 2000);
                        }}
                        className="w-full bg-gradient-to-r from-[#4B975B] to-[#5DAD6D] text-white py-3 sm:py-4 md:py-5 px-4 sm:px-6 md:px-8 rounded-lg sm:rounded-xl font-bold text-sm sm:text-base md:text-lg lg:text-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 sm:gap-3 md:gap-4 group"
                      >
                        {copiedResultCoupon ? (
                          <>
                            <svg className="w-5 h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Copied to Clipboard!
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 md:w-6 md:h-6 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy Coupon Code
                          </>
                        )}
                      </button>
                    </>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:4">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div className="text-xs sm:text-sm text-blue-800">
                        <p className="font-semibold mb-1">How to use your coupon on DaburShop.com:</p>
                        <ul className="list-disc list-inside space-y-0.5 sm:space-y-1 text-blue-700">
                          <li>Visit <a href="https://www.daburshop.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">www.daburshop.com</a></li>
                          <li>Copy the code above</li>
                          <li>Apply it at checkout</li>
                          <li>Enjoy your reward!</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="relative bg-gradient-to-r from-[#4B975B] to-[#5DAD6D] p-4 md:p-6 text-center overflow-hidden">
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-20 h-20 md:w-32 md:h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-28 h-28 md:w-40 md:h-40 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
                  </div>
                  <div className="mb-3 md:mb-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 bg-white/20 backdrop-blur-sm rounded-full">
                      <FontAwesomeIcon icon={faFaceFrown} className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 text-yellow-300" />
                    </div>
                  </div>

                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2 sm:mb-3">
                    Sorry
                  </h2>
                  <p className="text-white/90 text-base sm:text-lg lg:text-xl font-medium mb-1 sm:mb-2">
                    Better luck next time!
                  </p>
                  <p className="text-white/80 text-sm md:text-base">
                    {resultModalData.title}/ {moment(resultModalData.startTime).format("MMM DD, YYYY")}/ {resultModalData.type}
                  </p>
                </div>

                <div className="!p-2 sm:!p-6 lg:!p-8 space-y-4 md:space-y-6">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-300 rounded-xl sm:rounded-2xl !p-2 sm:!p-6 lg:!p-8">
                    <p className="text-center text-gray-700 font-semibold mb-3 md:mb-4">Numbers Don't Match</p>
                    <div className="flex items-center justify-center gap-3 md:gap-4">
                      <div className="text-center flex flex-col items-center justify-center">
                        <p className="text-xs md:text-sm lg:text-base text-gray-600 mb-1 md:mb-2">Your Number</p>
                        <div className="w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-gray-400 rounded-xl flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-lg md:text-xl lg:text-2xl">{resultModalData.selectedNumber}</span>
                        </div>
                      </div>
                      <svg className="w-6 h-6 md:w-8 md:h-8 text-red-500 animate-pulse mt-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div className="text-center flex flex-col items-center justify-center">
                        <p className="text-xs md:text-sm lg:text-base text-gray-600 mb-1 md:mb-2">Winning Number</p>
                        <div className="w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-red-500 rounded-xl flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-lg md:text-xl lg:text-2xl">{resultModalData.winningNumber}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl md:rounded-2xl !p-2 md:!p-6 text-center">
                    <svg className="w-10 h-10 md:w-12 md:h-12 text-blue-600 mx-auto mb-2 md:mb-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                    <p className="text-gray-700 font-semibold text-base md:text-lg lg:text-xl mb-1 md:mb-2">Don't Give Up!</p>
                    <p className="text-gray-600 text-sm md:text-base lg:text-lg">
                      Keep playing for more chances to win amazing prizes. Your next win could be just around the corner! 🌟
                    </p>
                  </div>

                  <button
                    onClick={() => setShowResultModal(false)}
                    className="w-full bg-gradient-to-r from-[#4B975B] to-[#5DAD6D] text-white py-3 md:py-4 lg:py-5 px-4 md:px-6 rounded-lg md:rounded-xl font-bold text-base md:text-lg lg:text-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )} */}
    </>
  );
};